
import { MachineComponentModel, MachineComponentStationModel, QueryOperator } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref } from 'vue-property-decorator';
import { machineComponentsStore, machineComponentStationsStore } from '@/libs/machine-components/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import MachineComponentSelectDialog from './MachineComponentSelectDialog.vue';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

@Component({
    components: { MachineComponentSelectDialog },
})
export default class StationMachineComponentsList extends Page {
    @Prop() stationId!: string;
    @Prop({ default: false }) compact!: boolean;
    @Ref() selectDialog!: any;

    loading = false;

    mounted() {
        this.loadData();
    }

    loadData() {
        this.loading = true;
        machineComponentStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(undefined, [{ field: 'stationId', op: QueryOperator.Eq, comparand: this.stationId }])
        );
        this.subscribe(machineComponentStationsStore, CrudReponse.GetAll)
            .then(() => {
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
            });
    }

    get componentStations(): MachineComponentStationModel[] {
        const items = (machineComponentStationsStore.useGetter(CrudGetter.Data) as MachineComponentStationModel[]) ?? [];
        return items.filter((x) => x.stationId === this.stationId);
    }

    get sortedComponentStations(): MachineComponentStationModel[] {
        return [...this.componentStations].sort((a, b) => {
            const componentA = this.getComponent(a.machineComponentId);
            const componentB = this.getComponent(b.machineComponentId);
            return (componentA?.code ?? '').localeCompare(componentB?.code ?? '');
        });
    }

    get allComponents(): MachineComponentModel[] {
        return (machineComponentsStore.useGetter(CrudGetter.Data) as MachineComponentModel[]) ?? [];
    }

    getComponent(componentId: string | undefined): MachineComponentModel | undefined {
        if (!componentId) return undefined;
        return this.allComponents.find((x) => x.id === componentId);
    }

    get currentComponentIds(): string[] {
        return this.componentStations.map(cs => cs.machineComponentId!);
    }

    openSelectDialog() {
        this.selectDialog.open(this.currentComponentIds);
    }

    onComponentsSelected(selectedComponents: MachineComponentModel[]) {
        const selectedIds = selectedComponents.map(c => c.id!);
        const currentIds = this.currentComponentIds;

        // Najít nově přidané
        const toAdd = selectedComponents.filter(c => !currentIds.includes(c.id!));
        
        // Najít odebrané
        const toRemove = this.componentStations.filter(cs => !selectedIds.includes(cs.machineComponentId!));

        const promises: Promise<any>[] = [];

        // Přidat nové komponenty
        toAdd.forEach((component) => {
            const componentStation: MachineComponentStationModel = {
                id: Guid.EmptyGuid(),
                machineComponentId: component.id,
                stationId: this.stationId,
            };
            const promise = new Promise((resolve, reject) => {
                machineComponentStationsStore.dispatch(CrudAction.Create, { item: componentStation });
                this.subscribe(machineComponentStationsStore, CrudReponse.Create)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        // Odebrat komponenty
        toRemove.forEach((componentStation) => {
            const promise = new Promise((resolve, reject) => {
                machineComponentStationsStore.dispatch(CrudAction.Delete, { id: componentStation.id });
                this.subscribe(machineComponentStationsStore, CrudReponse.Delete)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        Promise.all(promises).then(() => {
            this.loadData();
        });
    }
}
